import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { DEFAULT_DATE_FORMAT } from '@/components/constants';
import { useUpcomingSessionsList } from '@/features/upcomingSessionTab/hooks/useUpcomingSessionsList';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { formatWithMomentToken } from '@/utils/dates';
import { TypographyCaption, TypographyFootNote } from '@heidi/design-system/typography';
import { isValid } from 'date-fns';
import { useAtomValue } from 'jotai';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

function SessionBarStatusTextV2() {
  const openedSessionTab = useAtomValue(openedSessionTabAtom);
  const scheduleDate = useAtomValue(scheduleDateAtom);
  const { isPending: isUpcomingSessionsPending, upcomingSessions } =
    useUpcomingSessionsList();
  const isMobile = useIsMobile();
  const { data: user_settings_date_format } = useQueryUser(
    (data) => data?.session_settings?.date_format
  );
  const dateFormat = user_settings_date_format || DEFAULT_DATE_FORMAT;

  const statusText: ReactNode = useMemo(() => {
    const countResults = upcomingSessions?.length ?? 0;
    const formattedDate =
      scheduleDate && isValid(scheduleDate)
        ? formatWithMomentToken(scheduleDate, `${dateFormat} h:mma`)
        : '';
    if (isUpcomingSessionsPending) {
      return (
        <FormattedMessage id="JkgXOaWa6D" defaultMessage="Syncing appointments..." />
      );
    }
    if (scheduleDate) {
      return (
        <FormattedMessage
          id="i8IOIC/GzH"
          defaultMessage="{count, plural, one {# result} other {# results}} on {date}"
          values={{ count: countResults, date: formattedDate }}
        />
      );
    }
    return <FormattedMessage id="1KUeWjseA6" defaultMessage="Please select a date" />;
  }, [isUpcomingSessionsPending, scheduleDate, upcomingSessions, dateFormat]);

  if (!(openedSessionTab === openedSessionTabEnum.UPCOMING && scheduleDate)) {
    return <></>;
  }

  if (isMobile) {
    return (
      <TypographyCaption className="w-full text-center text-text-secondary">
        {statusText}
      </TypographyCaption>
    );
  } else {
    return (
      <TypographyFootNote className="w-full text-center text-text-secondary">
        {statusText}
      </TypographyFootNote>
    );
  }
}

SessionBarStatusTextV2.displayName = 'SessionBarStatusTextV2';
export default SessionBarStatusTextV2;
